<template>
    <v-layout row>
        <v-flex xs12 sm12>

            <!-- ERROR HANDLER -->
            <v-alert v-if="error" :value="true" color="error" icon="error" outline class="rounded-4">
                Error encountered. Please contact support. Error details: {{errorMessage}}
            </v-alert>  

            <!-- PROBATIONARY TO REGULAR -->
            <v-card v-if="probationaryToRegular" class="elevation-1">
                <v-toolbar @click="probationaryToRegularVisible=!probationaryToRegularVisible" class="clickable" flat height="45px">
                    <span class="font-weight-bold primary--text">PROBATIONARY TO REGULAR</span>
                    <v-spacer></v-spacer>
                    <v-btn icon>
                        <v-icon>{{ probationaryToRegularVisible ? 'expand_less' : 'expand_more' }}</v-icon>
                    </v-btn>
                </v-toolbar>

                <!-- Reminder Settings -->
                <v-card-text v-if="probationaryToRegularVisible" class="ml-20 mr-20">
                    <div class="body-2 text--secondary text--darken-2 mb-10 mr-30">
                        Set up company‑wide rules for transitioning employees from probationary to regular status after six months of continuous employment. 
                        The system can automatically update employment status or notify designated recipients in advance, ensuring smooth and compliant HR processes across the company.
                    </div>

                    <!-- Auto Update Switch -->
                    <v-layout row wrap v-if="!loading">
                        <v-flex xs12 sm12 class="form-row">            
                            <v-switch
                                v-model="probationaryToRegular.autoUpdateEmploymentStatus"
                                label="Automatically update Employment Status to Regular"
                                color="primary"
                                class="custom-switch"
                                hide-details
                                @change="updateSettings(probationaryToRegular)"
                            ></v-switch>
                            <div class="caption grey--text text--darken-2 mt-5">
                                Enable to automatically update status after six months of employment
                            </div>
                        </v-flex>
                    </v-layout>
                    
                    <!-- Notify Transitioning Employees Switch -->
                    <v-layout row wrap v-if="!loading">
                        <v-flex xs12 sm12 class="form-row">            
                            <v-switch
                                v-model="probationaryToRegular.notifyEmployee"
                                label="Notify Transitioning Employees"
                                color="primary"
                                class="custom-switch"
                                hide-details
                                @change="updateSettings(probationaryToRegular)"
                            ></v-switch>
                            <div class="caption grey--text text--darken-2 mt-5 mr-30">
                                Enable to send notifications directly to employees whose status is transitioning
                            </div>
                        </v-flex>
                    </v-layout>

                    <!-- Notify Other Recipients -->
                    <v-layout row wrap v-if="!loading">
                        <v-flex xs12 sm12 class="form-row">            
                            <v-switch
                                v-model="probationaryToRegular.notifyOtherRecipients"
                                label="Notify Other Recipients"
                                color="primary"
                                class="custom-switch"
                                hide-details
                                @change="updateSettings(probationaryToRegular)"
                            ></v-switch>
                            <div class="caption grey--text text--darken-2 mt-5 mr-30">
                                Enable to send notifications to other recipients when an employee's status is transitioning
                            </div>
                        </v-flex>
                    </v-layout>

                     <!-- Recipients -->
                    <v-layout row wrap v-if="!loading && probationaryToRegular.notifyOtherRecipients">
                        <v-flex xs12 sm12 class="form-row mt-20">
                            <v-layout row wrap justify-space-between align-center class="mb-1">
                                <span class="subheading font-weight-medium black--text ml-5">Other Recipients</span>                                    
                            </v-layout>
                            <div class="caption grey--text text--darken-2 mt-0 mr-30">
                                Select other employees or HR staff who should receive notifications
                            </div>
                            <v-chip v-for="(item, index) in probationaryToRegular.recipients" :key="index" class="chip-with-hover">
                                <v-avatar left>
                                    <v-img :src="awsS3Helper.getAvatarUrl(item.userId)"></v-img>
                                </v-avatar>
                                {{item.firstName}} {{item.lastName}}
                                <v-icon
                                    small
                                    class="remove-icon ml-2"
                                    title="Remove recipient"
                                    @click.stop="showRemoveRecipient(item, 'probationaryToRegular')"
                                >
                                    close
                                </v-icon>
                            </v-chip>
                            <v-btn fab small class="primary" @click="showOtherRecipientsForm('probationaryToRegular')">
                                <v-icon>add</v-icon>
                            </v-btn>
                        </v-flex>
                    </v-layout>

                    <!-- Notification Time -->
                    <v-layout row wrap v-if="!loading && (probationaryToRegular.notifyEmployee || probationaryToRegular.notifyOtherRecipients)">
                        <v-flex xs12 sm6 class="form-row mt-20">   
                            <v-layout row wrap justify-space-between align-center class="mb-1">
                                <span class="subheading font-weight-medium black--text ml-5">Notification Time</span>                                    
                            </v-layout>
                            <div class="caption body-subtext mb-2">
                                Select how many days before the probationary period ends notifications should be sent
                            </div>
                            <span class="body-2 font-weight-bold blue--text text--darken-3">
                                {{ getDaysText(probationaryToRegular.notifyBeforeDays) }}
                            </span>
                            <v-slider
                                v-model="probationaryToRegular.notifyBeforeDays"
                                min="0"
                                max="30"
                                step="1"
                                hide-details
                                color="blue darken-2"
                                track-color="rgba(0, 0, 0, 0.12)"
                                class="ml-5"
                                @change="updateSettings(probationaryToRegular)"
                            ></v-slider>
                            <div class="caption body-subtext mb-2">
                                Drag slider to adjust the number of days
                            </div>
                        </v-flex>
                    </v-layout>

                </v-card-text>
            </v-card>

            <!-- FIRST ANNIVERSARY -->
            <v-card v-if="firstAnniversary" class="elevation-1 mt-5">
                <v-toolbar @click="firstAnniversaryVisible=!firstAnniversaryVisible" class="clickable" flat height="45px">
                    <span class="font-weight-bold primary--text">FIRST ANNIVERSARY</span>
                    <v-spacer></v-spacer>
                    <v-btn icon>
                        <v-icon>{{ firstAnniversaryVisible ? 'expand_less' : 'expand_more' }}</v-icon>
                    </v-btn>
                </v-toolbar>

                <!-- Reminder Settings -->
                <v-card-text v-if="firstAnniversaryVisible" class="ml-20 mr-20">
                    <div class="body-2 text--secondary text--darken-2 mb-10 mr-30">
                        Set up company‑wide rules for recognizing employees on their first year of continuous employment. 
                        The system can automatically grant Service Incentive Leave (SIL) credits, notify transitioning employees, and alert designated recipients in advance. 
                        These settings help ensure compliance with labor requirements and support consistent HR practices across the company.
                    </div>
                
                    <!-- Auto Create SIL Credit Switch (Non-Pro-rated) -->
                    <v-layout row wrap v-if="!loading">
                        <v-flex xs12 sm12 class="form-row">            
                            <v-switch
                                v-model="firstAnniversary.autoCreateSILCredit"
                                label="Automatically grant 5 SIL credits (non‑pro‑rated)"
                                color="primary"
                                class="custom-switch"
                                hide-details
                                @change="updateSettings(firstAnniversary)"
                            ></v-switch>
                            <div class="caption grey--text text--darken-2 mt-5">
                                Enable automatic creation of 5 Service Incentive Leave (SIL) credits once employees reach their first year of continuous employment.
                            </div>
                        </v-flex>
                    </v-layout>

                    <!-- SIL Count -->
                    <v-layout row wrap v-if="!loading && firstAnniversary.autoCreateSILCredit">
                        <v-flex xs12 sm2 class="form-row mt-10">            
                            <v-text-field
                                v-model="firstAnniversary.silCount"
                                label="Number of SIL Credits (default 5)"
                                type="number"
                                min="1"
                                max="20"
                                :rules="[rules.required, rules.range]"
                                class="sil-count-field"
                                @change="updateSILCount()"
                            ></v-text-field>
                        </v-flex>
                        <v-flex xs12 sm6 class="form-row" :class="isMobile?'mt-n20':'mt-10'">
                            <v-checkbox 
                                v-model="firstAnniversary.convertUnusedSILToCash"
                                @change="updateSettings(firstAnniversary)"
                                label="Convert unused SIL to cash at year‑end"
                            ></v-checkbox>
                        </v-flex>
                    </v-layout>

                    <v-divider :class="isMobile ? 'divider-mobile' : 'divider'"></v-divider>

                    <!-- Pro-rated SIL Section -->
                    <v-layout row wrap v-if="!loading">
                        <v-flex xs12 sm12 class="form-row">            
                            <v-switch
                                v-model="firstAnniversary.autoCreateProRatedSIL"
                                label="Automatically grant pro‑rated SIL credits"
                                color="primary"
                                class="custom-switch"
                                hide-details
                                @change="updateSettings(firstAnniversary)"
                            ></v-switch>
                            <div class="caption grey--text text--darken-2 mt-5">
                                Enable automatic creation of pro‑rated Service Incentive Leave (SIL) credits (e.g., 5/12 credits) when employees reach their first anniversary of continuous employment.
                            </div>
                        </v-flex>
                    </v-layout>

                    <!-- Pro-rated SIL Count -->
                    <v-layout row wrap v-if="!loading && firstAnniversary.autoCreateProRatedSIL">
                        <v-flex xs12 sm2 class="form-row mt-10">            
                            <v-text-field
                                v-model="firstAnniversary.proRatedSilCount"
                                label="Pro‑rated SIL Credits"
                                type="number"
                                min="1"
                                max="20"
                                :rules="[rules.required, rules.range]"
                                class="sil-count-field"
                                @change="updateProRatedSILCount()"
                            ></v-text-field>
                        </v-flex>
                        <v-flex xs12 sm6 class="form-row" :class="isMobile?'mt-n20':'mt-10'">
                            <v-checkbox 
                                v-model="firstAnniversary.convertUnusedProRatedSILToCash"
                                @change="updateSettings(firstAnniversary)"
                                label="Convert unused pro‑rated SIL to cash at year‑end"
                            ></v-checkbox>
                        </v-flex>
                    </v-layout>

                    <v-divider :class="isMobile ? 'divider-mobile' : 'divider'"></v-divider>

                    <!-- Notify Transitioning Employees Switch -->
                    <v-layout row wrap v-if="!loading">
                        <v-flex xs12 sm12 class="form-row">            
                            <v-switch
                                v-model="firstAnniversary.notifyEmployee"
                                label="Notify Transitioning Employees"
                                color="primary"
                                class="custom-switch"
                                hide-details
                                @change="updateSettings(firstAnniversary)"
                            ></v-switch>
                            <div class="caption grey--text text--darken-2 mt-5 mr-30">
                                Enable to send notifications directly to employees whose status is transitioning
                            </div>
                        </v-flex>
                    </v-layout>

                    <!-- Notify Other Recipients -->
                    <v-layout row wrap v-if="!loading">
                        <v-flex xs12 sm12 class="form-row">            
                            <v-switch
                                v-model="firstAnniversary.notifyOtherRecipients"
                                label="Notify Other Recipients"
                                color="primary"
                                class="custom-switch"
                                hide-details
                                @change="updateSettings(firstAnniversary)"
                            ></v-switch>
                            <div class="caption grey--text text--darken-2 mt-5 mr-30">
                                Enable to send notifications to other recipients when an employee's status is transitioning
                            </div>
                        </v-flex>
                    </v-layout>

                     <!-- Recipients -->
                    <v-layout row wrap v-if="!loading && firstAnniversary.notifyOtherRecipients">
                        <v-flex xs12 sm12 class="form-row mt-20">
                            <v-layout row wrap justify-space-between align-center class="mb-1">
                                <span class="subheading font-weight-medium black--text ml-5">Other Recipients</span>                                    
                            </v-layout>
                            <div class="caption grey--text text--darken-2 mt-0 mr-30">
                                Select other employees or HR staff who should receive notifications
                            </div>
                            <v-chip v-for="(item, index) in firstAnniversary.recipients" :key="index" class="chip-with-hover">
                                <v-avatar left>
                                    <v-img :src="awsS3Helper.getAvatarUrl(item.userId)"></v-img>
                                </v-avatar>
                                {{item.firstName}} {{item.lastName}}
                                <v-icon
                                    small
                                    class="remove-icon ml-2"
                                    title="Remove recipient"
                                    @click.stop="showRemoveRecipient(item, 'firstAnniversary')"
                                >
                                    close
                                </v-icon>
                            </v-chip>
                            <v-btn fab small class="primary" @click="showOtherRecipientsForm('firstAnniversary')">
                                <v-icon>add</v-icon>
                            </v-btn>
                        </v-flex>
                    </v-layout>

                    <!-- Notification Time -->
                    <v-layout row wrap v-if="!loading && (firstAnniversary.notifyEmployee || firstAnniversary.notifyOtherRecipients)">
                        <v-flex xs12 sm6 class="form-row mt-20">   
                            <v-layout row wrap justify-space-between align-center class="mb-1">
                                <span class="subheading font-weight-medium black--text ml-5">Notification Time</span>                                    
                            </v-layout>
                            <div class="caption body-subtext mb-2">
                                Select how many days before the probationary period ends notifications should be sent
                            </div>
                            <span class="body-2 font-weight-bold blue--text text--darken-3">
                                {{ getDaysText(firstAnniversary.notifyBeforeDays) }}
                            </span>
                            <v-slider
                                v-model="firstAnniversary.notifyBeforeDays"
                                min="0"
                                max="30"
                                step="1"
                                hide-details
                                color="blue darken-2"
                                track-color="rgba(0, 0, 0, 0.12)"
                                class="ml-5"
                                @change="updateSettings(firstAnniversary)"
                            ></v-slider>
                            <div class="caption body-subtext mb-2">
                                Drag slider to adjust the number of days
                            </div>
                        </v-flex>
                    </v-layout>
                </v-card-text>
            </v-card>

        </v-flex>

        <v-dialog v-model="dialogAddOtherRecipients" width="600" persistent>   
            <add-other-recipients ref="addOtherRecipientsRef" :dialogAddOtherRecipients.sync="dialogAddOtherRecipients" @send="onAddOtherRecipients"></add-other-recipients>
        </v-dialog>
        <v-dialog v-model="dialogDelete" width="500">
            <delete ref="refDeleteComponent" okLabel="Remove" :dialogDelete.sync="dialogDelete" @okDelete="removeRecipient"></delete>
        </v-dialog>
    </v-layout> 
</template>

<script>

import employmentTransitionsApi from '@/api/employmenttransitions'
import { getExpandCollapseVisibility, updateExpandCollapseVisibility } from '@/helpers/user-prefs'
import awsS3Helper from '@/helpers/aws-s3'

export default {     
    name: 'EmploymentTransition',
    components: {
        'add-other-recipients': () => import('@/components/AutomatedReminders/AddOtherRecipients'),
        'delete': () => import('@/components/BasicPopups/Delete'),
    },   
    data () {
        const routeName = this.$route.name

        return {
            awsS3Helper,
            error: false,
            errorMessage: '',
            loading: false,
            disabled: false,
            dialogAddOtherRecipients: false,
            dialogDelete: false,
            rules: {
                required: v => !!v || 'Required.',
                range: v => (v >= 1 && v <= 20) || 'Must be between 1 and 20'
            },
            probationaryToRegularVisible: getExpandCollapseVisibility(routeName, 'employment-status-appearance'),
            firstAnniversaryVisible: getExpandCollapseVisibility(routeName, 'first-anniversary-appearance'),
            probationaryToRegular: null,
            firstAnniversary: null,
            initialized: false,
            forDeleteRecipient: null,

            // track which section is active
            currentTargetSection: null   
        }
    },
    methods: {
        getDaysText(days) {
            if (days === 0) {
                return 'On transition date'
            } else if (days === 1) {
                return '1 day before'
            } else {
                return days + ' days before'
            }
        },
        async getEmploymentTransitionSettings(show_loading) {
            try {
                this.loading = show_loading
                const response = await employmentTransitionsApi.getSettings()
                if (response && response.status == 200) {
                    this.probationaryToRegular = response.data.items.find(x => x.transitionType == 'ProbationaryToRegular')
                    this.firstAnniversary = response.data.items.find(x => x.transitionType == 'FirstAnniversary')
                }
                this.initialized = true
                this.loading = false
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        async updateSettings(data) {
            try {
                await employmentTransitionsApi.updateSettings(data)
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        }, 
        updateSILCount() {
            if (this.firstAnniversary.silCount > 1) {
                this.updateSettings(this.firstAnniversary)
            }
        },
        updateProRatedSILCount() {
            if (this.firstAnniversary.proRatedSilCount > 1) {
                this.updateSettings(this.firstAnniversary)
            }
        },
        onAddOtherRecipients(selectedRecipients) {
            const section = this[this.currentTargetSection]
            const selectedRecipientsTrimmed = selectedRecipients.map(({ userId, firstName, lastName }) => ({
                userId,
                firstName,
                lastName
            }))
            section.recipients.push(...selectedRecipientsTrimmed)
            this.updateSettings(section)
            this.dialogAddOtherRecipients = false
        },
        showOtherRecipientsForm(targetSection) {
            this.currentTargetSection = targetSection
            this.$refs.addOtherRecipientsRef.setData(this[targetSection].recipients)
            this.dialogAddOtherRecipients = true
        },
        showRemoveRecipient(recipient, targetSection) {
            this.currentTargetSection = targetSection
            this.forDeleteRecipient = recipient
            this.$refs.refDeleteComponent.setNote('')
            this.$refs.refDeleteComponent.setMessage(`Remove ${recipient.firstName} ${recipient.lastName} from the list of recipients?`)
            this.dialogDelete = true
        },
        removeRecipient() {
            const section = this[this.currentTargetSection]
            const index = section.recipients.findIndex(x => x.userId === this.forDeleteRecipient.userId)
            if (index !== -1) {
                section.recipients.splice(index, 1)
                this.updateSettings(section)
            }
            this.dialogDelete = false
        }
    },
    mounted() {
        this.getEmploymentTransitionSettings(true)
    },
    watch: {
        probationaryToRegularVisible(newVal) {
            updateExpandCollapseVisibility(this.$route.name, 'employment-status-appearance', newVal)
        },
        firstAnniversaryVisible(newVal) {
            updateExpandCollapseVisibility(this.$route.name, 'first-anniversary-appearance', newVal)
        },
    }
}
</script>
<style scoped>
.form-row {
    padding: 0px 15px 0px 15px;
    margin-right: 30px;
}
.divider {
    border-top: 1px solid #757575; /* darker gray */
    width: 95%;
    margin-top: 10px;
    margin-bottom: 10px;
}

.divider-mobile {
    border-top: 1px solid #757575; /* darker gray */
    width: 90%;
    margin-top: 10px;
    margin-bottom: 10px;
}
.notification-select {
    max-width: 320px;
}
.remove-icon {
  display: none;
  cursor: pointer;
}
.chip-with-hover:hover .remove-icon {
  display: inline-flex;
}

/* Scoped override for Vuetify 2 */
.custom-switch ::v-deep .v-input__slot .v-label {
    font-size: 15px !important; /* !important ensures Vuetify defaults are overridden */
    font-weight: 500;
    letter-spacing: 0.5px;
    color: rgba(0, 0, 0, 0.87);
}

.sil-count-field {
    max-width: 200px;
}
</style>